import React, { useContext, useEffect} from 'react';
import Date from '../../../../ui/Date';
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import SecondaryButton from '../../../../ui/SecondaryButton';
import InlineInputControlRow from '../../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../../ui/TextBox';
import { ApplicableTableSettings, TableSettings } from '../../../../tableSettings';
import { TextAlignLeftRenderer } from '../../../../uiControls/SmartTableRenderers';
import SmartTable from '../../../../uiControls/SmartTable';
import ApplicationContext from '../../../../ApplicationContext';
import { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import { AmsMbl } from '../../../../fms.core/ams/AmsMbl';
import _ from 'lodash';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';

const viewListSettings:TableSettings = {
  tableVersion: 1005,
  overflowX: 'hidden',
  overflowY: 'scroll',

  flexResizer: true,

  alternateRows: true,
  columns: [
    { accessor:'no', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 7.69, },
    { accessor:'containerNo', Header: 'Container #', visible: true, resizable: false, sortable: false, initialWidthPercentage: 25.64, },
    { accessor:'type', Header: 'ISO Type', visible: true, resizable: false, sortable: false, initialWidthPercentage: 20.51, },
    { accessor:'sealNo1', Header: 'Seal #', visible: true, resizable: false, sortable: false, initialWidthPercentage: 20.51, },
    { accessor:'sealNo2', Header: 'Seal #', visible: true, resizable: false, sortable: false, initialWidthPercentage: 20.51, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.14, },
  ]
};

interface ViewMblStorageKeys {
  modelStorage: string,
};

interface ViewMblParams {
  id: string | undefined,
  onPopupClose?: () => void,
};

interface ViewMblLoadedDetailsViewParams {
  mbl: AmsMbl,
  onPopupClose?: () => void
};

function getViewMblStorageKeys(): ViewMblStorageKeys {
  return {modelStorage: 'oi-ams-mbl'}
}

function ViewMblImpl(params: ViewMblParams) {
  const {id, onPopupClose} = params;
  const {modelStorage} = getViewMblStorageKeys();
  const [inStorage, setInStorage, removeMblFromStorage] = useTypedFastStorage<AmsMbl>(modelStorage, null);
  const appContext = useContext(ApplicationContext);

  useFunctionKeys({onEsc: cancel});

  function cancel() {
    if(onPopupClose) onPopupClose();
    return true;
  }

  useEffect(() => {
    initialize();
    return () => {
      removeMblFromStorage()
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  async function loadExisting() {
    const apiResult = await appContext.api.getAmsMbl(id);
    if(apiResult && apiResult.success) {
      let data: AmsMbl = new AmsMbl(apiResult.result);
      return data;
    }
    return {}
  }

  async function initialize() {
    const data = await loadExisting();
    setInStorage(_.cloneDeep(data));
  }

  if (!inStorage) {
    return null;
  }

  return (
    <ViewMblImplInitializedImpl 
      mbl={inStorage} 
      onPopupClose={params.onPopupClose}
    />
  );
}

function IndicatorRenderer({row}: any) {
  return <div style={{textAlign: 'center', width: '100%'}}>{row.index + 1}</div>;
}

function ViewMblImplInitializedImpl({
  mbl,
  onPopupClose
}: ViewMblLoadedDetailsViewParams) {

  const tableSettings:ApplicableTableSettings = {
    customTableId: 'oi-mblView',
    tableName: 'oi-mblView',
    ...viewListSettings,
    height: '200px',
    data: mbl.container.map(x => ({...x})),
    rowRenderers: [
      { accessor: 'no', Cell: IndicatorRenderer },
      { accessor: 'containerNo', Cell: TextAlignLeftRenderer },
      { accessor: 'type', Cell: TextAlignLeftRenderer },
      { accessor: 'sealNo1', Cell: TextAlignLeftRenderer },
      { accessor: 'sealNo2', Cell: TextAlignLeftRenderer },
      { accessor: 'scroll', Cell: TextAlignLeftRenderer },
    ],
  };

  return (
    <div className='report-popup'>
      <div style={{display: 'flex'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow mode="smaller-2x" smallMargin label="AMS User">
            <div style={{width: '70px'}}>
              <TextBox value={mbl.userName} disabled />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="MBL #">
            <TextBox value={mbl.mblNo} disabled />
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="Manifest Seq">
            <TextBox value={mbl.manifestSeq} disabled />
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="Vessel / Voyage #">
            <LeftRightSplit
              leftSize="calc(100% - 70px)"
              rightSize="70px"
              left={<TextBox value={mbl.vesselName} disabled />}
              right={<TextBox value={mbl.voyage} disabled />}
            />
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="Vessel Flag / Country">
            <div style={{width: '70px'}}>
              <TextBox value={mbl.vesselFlag} disabled />
            </div>
          </InlineInputControlRow>
        </div>

        <div style={{width: '320px', marginLeft: '40px'}}>
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Port of Loading">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={mbl.loadPort} disabled />}
              right={<TextBox value={mbl.lPortName} disabled />}
            />
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="ETD">
            <div style={{width: '70px'}}>
              <Date value={mbl.loadEtd} disabled />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="Port of Discharge">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={mbl.dCode} disabled />}
              right={<TextBox value={mbl.dPortName} disabled />}
            />
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="ETA">
            <div style={{width: '70px'}}>
              <Date value={mbl.eta} disabled />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow mode="smaller-2x" smallMargin label="SCAC">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={mbl.n1Scac} disabled />}
              right={<TextBox value={mbl.carrierName1} disabled />}
            />
          </InlineInputControlRow>
        </div>

      </div>

      <div style={{width: '390px', marginTop: '10px'}}>
        <SmartTable {...tableSettings} />
      </div>

      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          name='amsMbl-detail-cancel-button'
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const ViewMbl = ({onPopupClose, selectedElementId}: { onPopupClose?: () => void, selectedElementId: string }) => {

  return (
    <ViewMblImpl id={selectedElementId} onPopupClose={onPopupClose} key={`viewMbl-details`}/>
  );
};
export default ViewMbl;
